<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import SancarePasswordInput from '../design-system/atoms/SancarePasswordInput.vue'

const store = useStore()
const router = useRouter()
const { t } = useI18n()

const username = defineModel<string>('username', { default: '' })
const password = defineModel<string>('password',  { default: '' })
const newPassword = defineModel<string>('newPassword',  { default: '' })
const confirmationPassword = defineModel<string>('confirmationPassword', { default: '' })
const formError = ref<string>('')
const passwordInput = ref<InstanceType<typeof SancarePasswordInput> | null>(null)

const expiredPasswordRequest = computed(() => store.state.login.expiredPasswordRequest)
const expiredData = computed(() => store.state.login.expiredData)
const queryUsername = computed(() => store.state.route.query?.username || '')

onMounted(() => {
  store.commit('expiredPasswordRequestReset')
  username.value = expiredData.value.username || queryUsername.value
  passwordInput.value?.focus()
})

watch(expiredPasswordRequest, (request) => {
  if (request.ok) {
    password.value = ''
    newPassword.value = ''
    confirmationPassword.value = ''
    formError.value = null
    router.push('/login').then(() => store.dispatch('toast/setSuccess', t('login.passwordUpdated')))
  }
})

function checkData(): boolean {
  if (password.value === newPassword.value) {
    formError.value = 'Le nouveau mot de passe est identique à l\'ancien.'
    return false
  }
  if (newPassword.value && confirmationPassword.value) {
    if (newPassword.value !== confirmationPassword.value) {
      formError.value = 'Les mots de passe ne correspondent pas.'
      return false
    }
    if (newPassword.value.length < 8) {
      formError.value = 'Ce mot de passe est trop court'
      return false
    }
    if (!newPassword.value.match(/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).*$/)) {
      formError.value = 'Le mot de passe doit contenir au moins une minuscule, une majuscule et un chiffre.'
      return false
    }
  }
  if (!username.value || !password.value || !newPassword.value || !confirmationPassword.value) {
    formError.value = 'Tous les champs sont requis.'
    return false
  }

  formError.value = ''
  return true
}
function handleSubmit() {
  if (!checkData()) {
    return
  }
  formError.value = null
  store.dispatch('expiredPassword', {
    username: username.value,
    password: password.value,
    newPassword: newPassword.value,
    confirmationPassword: confirmationPassword.value,
  })
}
</script>

<template>
  <div class="form-extended">
    <form @submit.prevent="handleSubmit">
      <h1 class="unlogged-page-title mb-1">
        Mot de passe expiré
      </h1>
      <div class="text-center mb-3">
        <span class="small"> Votre mot de passe a expiré et doit être changé </span>
      </div>
      <div class="mb-3">
        <label
          class="small-label"
          for="username"
        >
          Identifiant
        </label>
        <input
          v-model="username"
          name="username"
          class="form-control login-input"
          required
        >
      </div>
      <div class="mb-3">
        <label
          class="small-label"
          for="password"
        >
          Mot de passe actuel
        </label>
        <sancare-password-input
          id="password"
          ref="passwordInput"
          v-model="password"
          name="password"
          class="login-input"
          required
          @blur="checkData"
        />
      </div>
      <div class="mb-3">
        <label
          class="small-label"
          for="newPassword"
        >
          Nouveau mot de passe
        </label>
        <sancare-password-input
          id="newPassword"
          v-model="newPassword"
          name="newPassword"
          class="login-input"
          pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).*$"
          title="Le mot de passe doit contenir au moins une minuscule, une majuscule et un chiffre."
          required
          minlength="8"
          @blur="checkData"
        />
      </div>
      <div class="mb-3">
        <label
          class="small-label"
          for="confirmationPassword"
        >
          Confirmation
        </label>
        <sancare-password-input
          id="confirmationPassword"
          v-model="confirmationPassword"
          name="confirmationPassword"
          class="login-input"
          pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).*$"
          title="Le mot de passe doit contenir au moins une minuscule, une majuscule et un chiffre."
          required
          minlength="8"
          @blur="checkData"
        />
      </div>
      <div class="text-end">
        <button
          class="btn btn-push btn-primary btn-padded"
          type="submit"
        >
          Valider
        </button>
      </div>
      <div class="unlogged-page-error-label">
        {{ formError }}
      </div>
      <div
        v-if="!expiredPasswordRequest.fetching && !expiredPasswordRequest.ok && expiredPasswordRequest.error"
        class="text-center text-danger"
      >
        <div>Identifiant ou mot de passe actuel incorrect.</div>
      </div>
    </form>
  </div>
</template>
